/**
 * ★★ TWO TABS OPEN ON ONE DRAFT — THE SCREEN THE PROTOTYPE DOES NOT HAVE.
 *
 * `docs/ui/04-pages.md:655-656` states it in as many words: *"The `409` conflict path is a screen,
 * not an error toast. It is 'a normal outcome, not an error screen', and it arrives carrying two
 * versions of the author's work."* §5 item 8 of the same file records that the prototype draws
 * nothing for it: *"Two tabs open on one draft has no answer drawn."*
 *
 * SO THIS IS A PANE WITH BOTH DOCUMENTS ON IT AND TWO BUTTONS, and the reason it can exist at all
 * is that the contract put the server's whole working copy in the 409 body rather than telling the
 * client to re-fetch: *"a conflict is precisely the case where the client's copy is the wrong one"*
 * and trimming it *"would force a second request at the moment the author is closest to losing
 * work"* (authors-api.yaml:1036-1039).
 *
 * ★ NOTHING IS MERGED AND NOTHING IS AUTO-PICKED. There is no three-way merge here and there must
 * not be: the platform stores one working copy per article with a single revision counter, so a
 * merged document would be a third version nobody wrote. The author reads both and chooses, and
 * ../use-editor-draft.ts does exactly what they chose.
 *
 * ★ THE AUTHOR'S TEXT IS NEVER DISCARDED BEFORE THE CHOICE. While this pane is up the autosave is
 * stopped — a retry would answer 409 again, and a retry that succeeded would be worse.
 */
import { Badge, Button, Group, Paper, Stack, Text, Title } from '@mantine/core';
import { blockText } from '../../../features/cabinet/api/editor-measure';
import type { Block, WorkingCopy } from '../../../features/cabinet/api/editor-api';
import { useTranslation } from '../../../i18n';
import type { EditorDraft } from './document';
import { usePieceContext } from './piece-context';

export interface ConflictPanelProps {
  /** What the author has in front of them, unsaved. */
  readonly mine: EditorDraft;
  /** The server's copy, exactly as the 409 carried it. */
  readonly theirs: WorkingCopy;
  readonly onTakeTheirs: () => void;
  readonly onKeepMine: () => void;
}

/** The document as one readable string, so two of them can be put side by side and compared. */
function asProse(title: string, subtitle: string, blocks: readonly Block[]): string {
  const parts = [title, subtitle];
  for (const block of blocks) {
    const text = blockText(block);
    parts.push(text === null ? '— — —' : text);
  }
  return parts.filter((part) => part.length > 0).join('\n\n');
}

function Side({
  heading,
  note,
  body,
  blockCount,
}: {
  heading: string;
  note: string;
  body: string;
  blockCount: number;
}) {
  const { t } = useTranslation('articles');
  return (
    <Paper component="section" withBorder p="md" radius="sm" style={{ flex: 1, minWidth: 280 }}>
      <Stack gap="xs">
        <Group justify="space-between">
          <Title order={3} size="h5">
            {heading}
          </Title>
          <Badge variant="default" size="sm" radius="sm">
            {t('editor.conflict.blocks', { count: blockCount })}
          </Badge>
        </Group>
        <Text size="xs" c="dimmed">
          {note}
        </Text>
        {/* `pre-wrap` rather than a diff: a character-level diff of two block documents is a
            different piece of work and a wrong one would be worse than none. */}
        <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
          {body}
        </Text>
      </Stack>
    </Paper>
  );
}

export function ConflictPanel({ mine, theirs, onTakeTheirs, onKeepMine }: ConflictPanelProps) {
  const { t } = useTranslation('articles');
  const context = usePieceContext();
  return (
    <Paper
      component="section"
      withBorder
      p="lg"
      radius="sm"
      aria-labelledby="editor-conflict-title"
      style={{
        backgroundColor: 'oklch(var(--mx-warning) / 0.08)',
        borderColor: 'oklch(var(--mx-warning) / 0.45)',
      }}
    >
      <Stack gap="md">
        <Stack gap={4}>
          <Title id="editor-conflict-title" order={2} size="h4">
            {t('editor.conflict.title')}
          </Title>
          <Text size="sm">{t('editor.conflict.body', { context })}</Text>
        </Stack>

        <Group align="stretch" wrap="wrap" gap="md">
          <Side
            heading={t('editor.conflict.mineHeading')}
            note={t('editor.conflict.mineNote')}
            body={asProse(mine.title, mine.subtitle, mine.blocks)}
            blockCount={mine.blocks.length}
          />
          <Side
            heading={t('editor.conflict.theirsHeading')}
            // Not "revision N": the counter moves on every save and tells an author nothing (design 15).
            note={t('editor.conflict.theirsNote')}
            body={asProse(theirs.title, theirs.subtitle, theirs.blocks)}
            blockCount={theirs.blocks.length}
          />
        </Group>

        <Group gap="sm" wrap="wrap">
          <Button onClick={onKeepMine}>{t('editor.conflict.keepMine')}</Button>
          <Button variant="default" onClick={onTakeTheirs}>
            {t('editor.conflict.takeTheirs')}
          </Button>
        </Group>
        <Text size="xs" c="dimmed">
          {t('editor.conflict.footnote', { context })}
        </Text>
      </Stack>
    </Paper>
  );
}
